import React from "react";
import styled, { keyframes } from "styled-components";
import GlitchImage from "../../../components/glitch-image/glitchImage";

const HeadingAnimation = keyframes`
  0% {
    top: 40%;
  }
  100% {
    top: 10px;
  }
`;

const SubHeadingAnimation = keyframes`
  0% {
    top: 55%;
    opacity: 1;
  }
  100% {
    top: 15%;
    opacity: 0;
    visibility: hidden;
  }
`;

const ContentTextAnimation = keyframes`
  0% {
    top: 60%;
    opacity: 0;
  }
  100% {
    top: 15%;
    opacity: 1;
  }
`;

const FontColor = keyframes`
  0%   { color: rgba(4, 229, 229, 1); }
  10%  { color: rgba(243, 112, 85, 1); }
  20%  { color: rgba(239, 78, 123, 1); }
  30%  { color: rgba(161, 102, 171, 1); }
  40%  { color: rgba(80, 115, 184, 1); }
  50%  { color: rgba(4, 229, 229, 1); }
  60%  { color: rgba(7, 179, 155, 1); }
  70%  { color: rgba(111, 186, 130, 1); }
  80%  { color: rgba(80, 115, 184, 1); }
  90%  { color: rgba(16, 152, 173, 1); }
  100% { color: rgba(243, 112, 85, 1); }
`;



const borderGlow = keyframes`
  0%, 100% { border-color: rgba(4, 229, 229, 0.15); box-shadow: 0 0 5px rgba(4, 229, 229, 0.03); }
  50% { border-color: rgba(4, 229, 229, 0.5); box-shadow: 0 0 18px rgba(4, 229, 229, 0.12); }
`;

const scanlineScroll = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 0 100%; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const TextContainer = styled.div`
  background-color: #050505;
`;

const ProjectIndex = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 220px;
  font-weight: 900;
  color: rgba(4, 229, 229, 0.08);
  font-family: 'Courier New', monospace;
  z-index: 0;
  pointer-events: none;
  transition: opacity 0.5s;
  letter-spacing: -10px;
  &.hidden { opacity: 0; }
`;

const CornerBrackets = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none;
  z-index: 1;
  transition: opacity 0.5s;
  &.hidden { opacity: 0; }
  &::before, &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: rgba(4, 229, 229, 0.4);
    border-style: solid;
  }
  &::before { top: 15px; left: 15px; border-width: 2px 0 0 2px; }
  &::after { bottom: 15px; right: 15px; border-width: 0 2px 2px 0; }
`;

const Scanlines = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none;
  z-index: 1;
  border-radius: 10px;
  overflow: hidden;
  transition: opacity 0.5s;
  &.hidden { opacity: 0; }
  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; width: 100%; height: 200%;
    background: repeating-linear-gradient(
      0deg,
      transparent, transparent 3px,
      rgba(4, 229, 229, 0.015) 3px, rgba(4, 229, 229, 0.015) 4px
    );
    animation: ${scanlineScroll} 8s linear infinite;
  }
`;

const Text = styled.div`
  border-radius: 10px;
  border: 0.1rem solid rgba(4, 229, 229, 0.3);
  position: absolute;
  height: 100%; width: 100%; top: 0;
  align-items: center;
  flex-direction: column;
  justify-content: flex-start;
  transition: ease-in-out 0.5s;
  text-align: center;
  display: flex;
  animation: ${borderGlow} 4s ease-in-out infinite;
  &.active {
    transition: ease-in-out 0.5s;
    animation: none;
    border-color: rgba(4, 229, 229, 0.5);
    box-shadow: 0 0 20px rgba(4, 229, 229, 0.15);
    background-image: linear-gradient(
      to top,
      rgba(4, 229, 229, 0.3),
      rgba(255, 255, 255, 0)
    );
  }
`;

const Heading = styled.h4`
  position: absolute;
  top: 40%;
  font-weight: 600;
  font-size: 30px;
  text-align: center;
  @media (max-width: 767px) {
    font-size: 20px;
  }
  animation: ${FontColor} 10s infinite alternate;
  &.active {
    animation: ${HeadingAnimation} 0.5s forwards, ${FontColor} 10s infinite alternate;
    padding-top: 20px;
    animation-fill-mode: forwards;
  }
`;

const SubHeading = styled.h5`
  position: absolute;
  top: 55%;
  font-size: 16px;
  font-weight: 400;
  text-transform: uppercase;
  text-align: center;
  color: rgba(4, 229, 229, 0.4);
  font-family: 'Courier New', monospace;
  letter-spacing: 3px;
  animation: ${FontColor} 10s infinite alternate;
  @media (max-width: 767px) {
    font-size: 12px;
  }
  &::after {
    content: '_';
    animation: ${blink} 1s step-end infinite;
    margin-left: 2px;
  }
  &.active {
    animation: ${SubHeadingAnimation} 0.5s forwards;
    top: 15%;
    opacity: 0;
    visibility: hidden;
    animation-fill-mode: forwards;
    &::after { display: none; }
  }
`;

const ContentText = styled.div`
  font-size: 14px;
  font-weight: 300;
  width: 100%;
  position: absolute;
  top: 60%;
  opacity: 0;
  color: #c5c5c5;
  padding: 10px;
  &.active {
    animation: ${ContentTextAnimation} 0.5s forwards;
    top: 15%;
    bottom: 20px;
    opacity: 1;
    animation-fill-mode: forwards;
    display: flex;
    flex-direction: column;
    @media (max-width: 1025px) {
      bottom: 60px;
    }
  }
`;

const FullWidthImageContainer = styled.div`
  width: 100%;
  padding: 0 15px;
  margin-top: 10px;
  flex: 1;
  min-height: 0;
  & > div {
    height: 100%;
    border-radius: 8px;
    overflow: hidden;
  }
`;

const DescriptionWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 5px;
`;

const TechTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding: 15px 10px 20px;
  width: 100%;
`;

const TechTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 400;
  color: #04e5e5;
  border: 1px solid rgba(4, 229, 229, 0.6);
  border-radius: 20px;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
  transition: all 0.3s;
  &:hover {
    border-color: rgba(4, 229, 229, 0.7);
    box-shadow: 0 0 8px rgba(4, 229, 229, 0.2);
    color: #04e5e5;
  }
`;

const arrowBounce = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(8px); }
`;

const CardFooterLink = styled.div`
  display: none;
  @media (max-width: 1025px) {
    display: flex;
  }
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50px;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    to top,
    rgba(4, 229, 229, 0.2) 0%,
    rgba(4, 229, 229, 0.05) 60%,
    transparent 100%
  );
  border-top: 1px solid rgba(4, 229, 229, 0.2);
  border-radius: 0 0 10px 10px;
  color: #04e5e5;
  text-shadow: 0 0 8px rgba(4, 229, 229, 0.4);
  font-size: 14px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-decoration: none;
  transition: all 0.3s;
  cursor: pointer;
  z-index: 5;
  .arrow {
    display: inline-block;
    margin-left: 8px;
    animation: ${arrowBounce} 1s ease-in-out infinite;
  }
  &:hover {
    background: linear-gradient(
      to top,
      rgba(4, 229, 229, 0.35) 0%,
      rgba(4, 229, 229, 0.1) 60%,
      transparent 100%
    );
    color: #04e5e5;
    text-decoration: none;
  }
`;

// Lightweight native scramble effect (replaces heavy baffle-react)
class NativeScramble extends React.Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.reveal && this.props.reveal) {
      this.runScramble();
    }
  }

  runScramble() {
    const el = this.ref.current;
    if (!el) return;
    const text = this.props.text;
    const chars = 'abcdefghijklmnopqrstuvwxyz1234567890';
    const duration = 1000;
    const stepTime = 50;
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += stepTime;
      const progress = Math.min(elapsed / duration, 1);
      let scrambled = '';
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ' || text[i] === '\n') {
          scrambled += text[i];
        } else if (Math.random() < progress) {
          scrambled += text[i];
        } else {
          scrambled += chars[Math.floor(Math.random() * chars.length)];
        }
      }
      if (el) el.innerText = scrambled;
      if (progress === 1) {
        clearInterval(interval);
        if (el) el.innerText = text;
      }
    }, stepTime);
    this._interval = interval;
  }

  componentWillUnmount() {
    if (this._interval) clearInterval(this._interval);
  }

  render() {
    // Before reveal, show scrambled static text; after reveal, the interval takes over
    const text = this.props.text;
    const chars = 'abcdefghijklmnopqrstuvwxyz1234567890';
    const display = this.props.reveal ? text : text.split('').map(c => (c === ' ' || c === '\n') ? c : chars[Math.floor(Math.random() * chars.length)]).join('');
    return <span ref={this.ref}>{display}</span>;
  }
}

class DesktopContent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      show: false,
    };
  }

  componentDidMount() {
    if (typeof window !== "undefined" && window.innerWidth <= 1025) {
      this.setState({ show: true });
    }
  }

  render() {
    return (
      <TextContainer
        onMouseEnter={() => {
          !this.state.show && this.setState({ show: true });
        }}
      >
        <Text
          className={this.state.show ? "active" : ""}
          style={{ display: "flex" }}
        >
          <Scanlines className={this.state.show ? 'hidden' : ''} />
          <CornerBrackets className={this.state.show ? 'hidden' : ''} />
          <ProjectIndex className={this.state.show ? 'hidden' : ''}>
            {String(this.props.index !== undefined ? this.props.index + 1 : '').padStart(2, '0')}
          </ProjectIndex>
          <Heading className={this.state.show ? "active" : ""}>
            {this.props.text}
          </Heading>
          <SubHeading className={this.state.show ? "active" : ""}>
            {this.props.category}
          </SubHeading>
          <ContentText className={this.state.show ? "active" : ""}>
            <DescriptionWrapper>
              <span style={{ visibility: "hidden" }}>{this.props.description}</span>
              <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", overflow: "hidden", wordBreak: "break-word" }}>
                <NativeScramble text={this.props.description} reveal={this.state.show} />
              </div>
            </DescriptionWrapper>

            <FullWidthImageContainer>
              <GlitchImage src={this.props.image} />
            </FullWidthImageContainer>
            <TechTags>
              {this.props.tech.map((value, index) => (
                <TechTag key={index}>{value}</TechTag>
              ))}
            </TechTags>
          </ContentText>
          <CardFooterLink onClick={() => window.open(this.props.link, '_blank')}>
            View Project <span className="arrow">→</span>
          </CardFooterLink>
        </Text>
      </TextContainer>
    );
  }
}

export default DesktopContent;
